import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Table,
    Badge,
    Navbar,
    NavbarBrand,
    Nav,
    NavItem
} from 'reactstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { apiURL1 } from '../../config';
import {
    createLandscapePdf,
    standardAutoTableOptions,
    drawCenteredFooter,
} from '../../utils/pdfLayout';

const PublicResult = () => {
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [classId, setClassId] = useState('');
    const [examType, setExamType] = useState('');
    const [year, setYear] = useState(dayjs().year());
    const [classes, setClasses] = useState([]);
    const [examTypes, setExamTypes] = useState([]);
    const [schoolInfo, setSchoolInfo] = useState({
        schoolName: "Ummu Dhardha Ladies Islamic college",
        schoolAddress: "Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.",
        schoolPhoneNumber: "+94 67 205 3211",
        schoolEmail: "info@ummudhardha.lk"
    });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const baseURL = apiURL1;
    const logoUrl = "https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp";

    useEffect(() => {
        fetchClasses();
        fetchExamTypes();
        fetchSchoolInfo();
    }, []);

    const fetchClasses = async () => {
        try {
            const response = await axios.get(`${baseURL}/publicRoute/allClass`);
            setClasses(response.data);
        } catch (error) {
            // console.error('Error fetching classes:', error);
            toast.error('Error fetching classes');
        }
    };

    const fetchExamTypes = async () => {
        try {
            const response = await axios.get(`${baseURL}/publicRoute/examType`);
            setExamTypes(response.data.data);
        } catch (error) {
            // console.error('Error fetching exam types:', error);
            toast.error('Error fetching exam types');
        }
    };

    const fetchSchoolInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/publicRoute/schoolInfo`);
            // console.log('response.data is ', response.data);

            // Ensure it's an array and has at least one element
            if (Array.isArray(response.data) && response.data.length > 0) {
                setSchoolInfo(response.data[0]); // Set the first element as schoolInfo
            } else {
                // console.error("Unexpected data format:", response.data);
            }
        } catch (error) {
            // console.error('Error fetching school info:', error);
            // Handle error gracefully (fallback values are already in initial state)
        }
    };


    const handleViewResult = async () => {
        if (!registrationNumber || !classId || !examType || !year) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(
                `${baseURL}/publicRoute/viewResult?registrationNumber=${registrationNumber}&classId=${classId}&type=${examType}&year=${year}`
            );

            if (response.data.success) {
                setResult(response.data.data);
                toast.success('Result fetched successfully');
            } else {
                setResult(null);
                handleResultError(response.data.message, response.status);
            }
        } catch (error) {
            setResult(null);
            if (error.response) {
                const { status, data } = error.response;
                handleResultError(data?.message, status);
            } else {
                toast.error('Unable to reach the server. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Map API messages to clear user-facing feedback
    const handleResultError = (message, status) => {
        const msg = (message || '').toString();
        const lower = msg.toLowerCase();

        if (status === 400 || lower.includes('all fields are required')) {
            toast.error('Please fill in all required fields.');
            return;
        }
        if (status === 404 && (lower.includes('student not found') || lower.includes('no student'))) {
            toast.error('No result found. Student with this registration number was not found.');
            return;
        }
        if (lower.includes('no exams found') || lower.includes('no exam')) {
            toast.error('No result found for the selected class, exam type, and year.');
            return;
        }
        if (
            status === 403 ||
            lower.includes('awaiting') ||
            lower.includes('not found') ||
            lower.includes('no result')
        ) {
            toast.info(
                lower.includes('awaiting')
                    ? 'Result is awaiting. Please check again later.'
                    : 'No result found for the selected details.'
            );
            return;
        }
        if (status === 500) {
            toast.error('No result found. Please verify the details and try again.');
            return;
        }
        toast.error(msg || 'No result found for the selected details.');
    };


    const getGradeColor = (grade) => {
        if (!grade) return 'secondary';
        const gradeUpper = grade.toUpperCase();
        if (gradeUpper === 'A' || gradeUpper === 'A+') return 'success';
        if (gradeUpper === 'B' || gradeUpper === 'B+') return 'info';
        if (gradeUpper === 'C' || gradeUpper === 'C+') return 'primary';
        if (gradeUpper === 'D' || gradeUpper === 'D+') return 'warning';
        if (gradeUpper === 'F') return 'danger';
        return 'secondary';
    };

    const getOverallColor = (percentage) => {
        if (percentage >= 90) return '#28a745';
        if (percentage >= 80) return '#17a2b8';
        if (percentage >= 70) return '#007bff';
        if (percentage >= 60) return '#ffc107';
        if (percentage >= 50) return '#fd7e14';
        return '#dc3545';
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const handlePrint = () => {
        const printContent = `
        <html>
            <head>
                <title>Result - ${result.fullName}</title>
                <style>
                    body { 
                        font-family: Arial, sans-serif; 
                        margin: 0;
                        padding: 20px;
                    }
                    .header { 
                        text-align: center; 
                        margin-bottom: 30px; 
                        border-bottom: 2px solid #003366;
                        padding-bottom: 15px;
                    }
                    .logo {
                        width: 100px;
                        height: auto;
                        margin-bottom: 10px;
                    }
                    .school-name {
                        color: #003366;
                        margin: 5px 0;
                        font-size: 24px;
                    }
                    .school-info { 
                        margin-bottom: 10px;
                        font-size: 14px;
                        color: #555;
                    }
                    .result-title {
                        margin-top: 20px;
                        color: #003366;
                        border-bottom: 1px solid #ddd;
                        padding-bottom: 5px;
                    }
                    .student-info {
                        margin: 20px 0;
                        padding: 15px;
                        border: 1px solid #ddd;
                        border-radius: 5px;
                        background-color: #f9f9f9;
                    }
                    .result-table { 
                        width: 100%; 
                        border-collapse: collapse; 
                        margin: 20px 0; 
                    }
                    .result-table th, .result-table td { 
                        border: 1px solid #ddd; 
                        padding: 10px; 
                        text-align: left; 
                    }
                    .result-table th { 
                        background-color: #003366; 
                        color: white;
                    }
                    .result-table tr:nth-child(even) {
                        background-color: #f2f2f2;
                    }
                    .summary {
                        margin-top: 20px;
                        padding: 15px;
                        border: 1px solid #ddd;
                        border-radius: 5px;
                        background-color: #f9f9f9;
                    }
                    .footer {
                        margin-top: 40px;
                        text-align: center;
                        font-size: 12px;
                        color: #777;
                        border-top: 1px solid #ddd;
                        padding-top: 10px;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png" alt="School Logo" class="logo"/>
                    <h2 class="school-name">${schoolInfo.schoolName}</h2>
                    <div class="school-info">
                        <p>${schoolInfo.schoolAddress}</p>
                        <p>Tel: ${schoolInfo.schoolPhoneNumber} | Email: ${schoolInfo.schoolEmail}</p>
                    </div>
                    <h1 class="result-title">Examination Result</h1>
                </div>
                <div class="student-info">
                    <p><strong>Student Name:</strong> ${result.fullName || 'N/A'}</p>
                    <p><strong>Registration Number:</strong> ${result?.registrationNumber || 'N/A'}</p>
                    <p><strong>Class:</strong> ${classes.find(c => c._id === result.class)?.className || 'N/A'}</p>
                    <p><strong>Exam Type:</strong> ${examTypes.find(e => e._id === result.examType)?.name || 'N/A'}</p>
                    <p><strong>Academic Year:</strong> ${result?.year || 'N/A'}</p>
                </div>
                <table class="result-table">
                    <thead>
                        <tr>
                            ${(() => {
                                const isComponent = Array.isArray(result.subjects) && result.subjects.some(s => Array.isArray(s.components) && s.components.length > 0);
                                if (!isComponent) {
                                    return `
                                        <th>Subject</th>
                                        <th>Total Marks</th>
                                        <th>Obtained Marks</th>
                                        <th>Grade</th>
                                        <th>Remarks</th>
                                    `;
                                }
                                const set = new Set();
                                result.subjects.forEach(subject => {
                                    (subject.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
                                    (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
                                });
                                const compHeaders = Array.from(set).map(n => `<th>${n}</th>`).join('');
                                return `
                                    <th>Subject</th>
                                    <th>Subject Mid Marks</th>
                                    ${compHeaders}
                                    <th>Subject Final Marks</th>
                                    <th>Total Marks</th>
                                    <th>Total Obtained</th>
                                    <th>Grade</th>
                                    <th>Remarks</th>
                                `;
                            })()}
                        </tr>
                    </thead>
                    <tbody>
                        ${(() => {
                            const isComponent = Array.isArray(result.subjects) && result.subjects.some(s => Array.isArray(s.components) && s.components.length > 0);
                            if (!isComponent) {
                                return result.subjects.map(subject => {
                                    const totalMarks = subject?.grandTotal ?? subject?.subjectTotalMarks ?? subject?.totalMarks ?? result?.totalMarks ?? '-';
                                    const obtainedMarks = subject?.grandObtained ?? subject?.outsideObtained ?? subject?.obtainedMarks ?? result?.obtainedMarks ?? '-';
                                    return `
                                        <tr>
                                            <td>${subject?.subjectName || 'N/A'}</td>
                                            <td>${totalMarks}</td>
                                            <td>${obtainedMarks}</td>
                                            <td>${subject?.grade || 'N/A'}</td>
                                            <td>${subject?.remarks || '-'}</td>
                                        </tr>
                                    `;
                                }).join('');
                            }
                            const set = new Set();
                            result.subjects.forEach(subject => {
                                (subject.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
                                (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) set.add(n); });
                            });
                            const names = Array.from(set);

                            return result.subjects.map(subject => {
                                const totals = new Map();
                                (subject.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) totals.set(n, c?.totalMarks ?? '-'); });
                                const obtained = new Map();
                                (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) obtained.set(n, c?.marks ?? '-'); });
                                const subjectMid = `${(subject.midSubjectObtainedMarks ?? '-')}/${(subject.midSubjectTotalMarks ?? '-')}`;
                                const compCells = names.map(n => `<td>${(obtained.get(n) ?? '-')}/${(totals.get(n) ?? '-')}</td>`).join('');
                                const subjectFinal = `${(subject.subjectObtainMarksFinal ?? '-')}/${(subject.subjectTotalMarksFinal ?? '-')}`;
                                const totalMarks = subject.grandTotal ?? subject.totalMarks ?? '-';
                                const totalObtained = subject.grandObtained ?? subject.obtainedMarks ?? '-';
                                return `
                                    <tr>
                                        <td>${subject?.subjectName || 'N/A'}</td>
                                        <td>${subjectMid}</td>
                                        ${compCells}
                                        <td>${subjectFinal}</td>
                                        <td>${totalMarks}</td>
                                        <td>${totalObtained}</td>
                                        <td>${subject?.grade || 'N/A'}</td>
                                        <td>${subject?.remarks || '-'}</td>
                                    </tr>
                                `;
                            }).join('');
                        })()}
                    </tbody>
                </table>
                <div class="summary">
                    <p><strong>Total Marks:</strong> ${result?.totalMarks || 'N/A'}</p>
                    <p><strong>Obtained Marks:</strong> ${result?.obtainedMarks || 'N/A'}</p>
                    <p><strong>Overall Percentage:</strong> ${result?.overallPercentage || 'N/A'}%</p>
                    <p><strong>Overall Grade:</strong> ${result?.overallGrade || 'N/A'}</p>
                    <p><strong>Overall Rank (Class):</strong> ${result?.rank ?? 'N/A'}</p>
                </div>
                <div class="footer">
                    <p>This is an official result document from ${schoolInfo?.schoolName || 'N/A'}.</p>
                    <p>Date Printed: ${formatDate(new Date())}</p>
                </div>
            </body>
        </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

    const handleDownloadPdf = async () => {
        const response = await axios.get(`${baseURL}/publicRoute/schoolInfo`);

        const pdf = createLandscapePdf(jsPDF);
        const pageWidth = pdf.internal.pageSize.getWidth();

        const schoolName = response.data.schoolName || "Ummu Dhardha Ladies Islamic College";
        const schoolAddress = response.data.schoolAddress || "Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka";
        const schoolPhone = response.data.schoolPhoneNumber || "+ 94 67 205 3211";
        const schoolEmail = response.data.schoolEmail || "info@ummudhardha.lk";

        const studentName = result.fullName || "Unknown Student";
        const registrationNumber = result.registrationNumber || "N/A";
        const className = classes.find(c => c._id === result.class)?.className || "N/A";
        const examType = examTypes.find(e => e._id === result.examType)?.name || "N/A";
        const academicYear = result.year || "N/A";
        const classRank = result.rank ?? '-';

        const logoUrlPdf = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";
        try {
            const imgResponse = await axios.get(logoUrlPdf, { responseType: 'arraybuffer' });
            const base64Logo = `data:image/webp;base64,${btoa(
                new Uint8Array(imgResponse.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
            )}`;
            pdf.addImage(base64Logo, 'PNG', 12, 8, 22, 22);
        } catch (error) { /* logo optional */ }

        pdf.setFontSize(14);
        pdf.setTextColor(0, 51, 102);
        pdf.setFont('helvetica', 'bold');
        pdf.text(schoolName, 40, 14);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        pdf.text(schoolAddress, 40, 20);
        pdf.text(`Tel: ${schoolPhone} | Email: ${schoolEmail}`, 40, 25);

        pdf.setFontSize(12);
        pdf.setTextColor(0, 51, 102);
        pdf.setFont('helvetica', 'bold');
        pdf.text("EXAMINATION RESULT", pageWidth / 2, 34, { align: 'center' });

        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'normal');
        let y = 42;
        pdf.text(`Student Name: ${studentName}`, 12, y);
        pdf.text(`Registration Number: ${registrationNumber}`, pageWidth / 2 + 10, y);
        y += 5;
        pdf.text(`Class: ${className}`, 12, y);
        pdf.text(`Exam Type: ${examType}`, pageWidth / 2 + 10, y);
        y += 5;
        pdf.text(`Academic Year: ${academicYear}`, 12, y);
        pdf.text(`Overall Rank (Class): ${classRank}`, pageWidth / 2 + 10, y);
        y += 8;

        const tableColumn = ["Subject", "Mid", "Final", "Total", "Obtained", "Grade"];
        const tableRows = [];

        if (result.subjects && Array.isArray(result.subjects)) {
            result.subjects.forEach((subject) => {
                tableRows.push([
                    subject?.subjectName || "N/A",
                    `${subject.midSubjectObtainedMarks ?? '-'}/${subject.midSubjectTotalMarks ?? '-'}`,
                    `${subject.subjectObtainMarksFinal ?? '-'}/${subject.subjectTotalMarksFinal ?? '-'}`,
                    subject.grandTotal ?? subject.totalMarks ?? '-',
                    subject.grandObtained ?? subject.obtainedMarks ?? '-',
                    subject?.grade || "N/A",
                ]);
            });
        }

        pdf.autoTable(standardAutoTableOptions({
            startY: y,
            head: [tableColumn],
            body: tableRows,
            headStyles: { fillColor: [0, 51, 102], textColor: 255, fontSize: 7, fontStyle: 'bold', halign: 'center' },
            columnStyles: {
                0: { cellWidth: 70, halign: 'left' },
                1: { cellWidth: 30 },
                2: { cellWidth: 30 },
                3: { cellWidth: 28 },
                4: { cellWidth: 28 },
                5: { cellWidth: 22 },
            },
        }));

        const finalY = pdf.lastAutoTable.finalY + 8;
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Total Marks: ${result.totalMarks || "N/A"}`, 12, finalY);
        pdf.text(`Obtained Marks: ${result.obtainedMarks || "N/A"}`, 12, finalY + 5);
        pdf.text(`Overall Percentage: ${result.overallPercentage || "N/A"}%`, 12, finalY + 10);
        pdf.text(`Overall Grade: ${result.overallGrade || "N/A"}`, 12, finalY + 15);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Overall Rank within Class: ${classRank}`, 12, finalY + 22);

        drawCenteredFooter(pdf, schoolName);
        pdf.save(`result-${studentName.replace(/\s+/g, '-')}-${registrationNumber}.pdf`);
    };


    return (
        <div className="bg-light min-vh-100">
            {/* Header with Logo and School Info */}
            <Navbar color="dark" dark expand="md" className="mb-4 py-3">
                <Container>
                    <NavbarBrand href="/" className="d-flex align-items-center text-white">
                        <img src={logoUrl} alt="School Logo" style={{ height: '50px', marginRight: '15px' }} />
                        <div>
                            <h4 className="m-0 text-white">{schoolInfo.schoolName}</h4>
                            <small className="text-light">Student Result Portal</small>
                        </div>
                    </NavbarBrand>
                    <Nav className="ml-auto" navbar>
                        <NavItem>
                            <div className="text-white text-right">
                                <small className="d-block">{schoolInfo.schoolPhoneNumber}</small>
                                <small>{schoolInfo.schoolEmail}</small>
                            </div>
                        </NavItem>
                    </Nav>
                </Container>
            </Navbar>


            <Container className="py-4">
                <Row>
                    <Col lg="5" className="mb-4">
                        <Card className="shadow border-0 h-100">
                            <CardHeader className="bg-primary text-white border-0">
                                <h4 className="mb-0">Enter Your Details</h4>
                            </CardHeader>
                            <CardBody className="bg-white">
                                <p className="text-muted mb-4">Please provide your registration details to view your examination results.</p>

                                <FormGroup>
                                    <Label className="font-weight-bold">Registration Number *</Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter your registration number"
                                        value={registrationNumber}
                                        onChange={(e) => setRegistrationNumber(e.target.value)}
                                        className="form-control-lg"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label className="font-weight-bold">Class *</Label>
                                    <Input
                                        type="select"
                                        value={classId}
                                        onChange={(e) => setClassId(e.target.value)}
                                        className="form-control-lg"
                                    >
                                        <option value="">Select Class</option>
                                        {classes.map((c) => (
                                            <option key={c._id} value={c._id}>
                                                {c.className}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label className="font-weight-bold">Exam Type *</Label>
                                    <Input
                                        type="select"
                                        value={examType}
                                        onChange={(e) => setExamType(e.target.value)}
                                        className="form-control-lg"
                                    >
                                        <option value="">Select Exam Type</option>
                                        {examTypes.map((e) => (
                                            <option key={e._id} value={e._id}>
                                                {e.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label className="font-weight-bold">Academic Year</Label>
                                    <Input
                                        type="select"
                                        value={year}
                                        onChange={(e) => setYear(e.target.value)}
                                        className="form-control-lg"
                                    >
                                        {Array.from({ length: 11 }, (_, i) => dayjs().year() - 5 + i).map((y) => (
                                            <option key={y} value={y}>
                                                {y}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>

                                <div className="d-grid gap-2 mt-4">
                                    <Button
                                        color="primary"
                                        size="lg"
                                        block
                                        onClick={handleViewResult}
                                        disabled={loading}
                                        className="py-3"
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Fetching Results...
                                            </>
                                        ) : (
                                            'View Result'
                                        )}
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg="7">
                        {result ? (
                            <Card className="shadow border-0">
                                <CardHeader className="bg-primary text-white d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">Result Card</h4>
                                    <div>
                                        <Button color="light" size="sm" onClick={handlePrint} className="me-2">
                                            <i className="fa fa-print me-1"></i> Print
                                        </Button>
                                        <Button color="light" size="sm" onClick={handleDownloadPdf}>
                                            <i className="fa fa-download me-1"></i> Download PDF
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardBody className="bg-white">
                                    <div className="text-center mb-4">
                                        <img src={logoUrl} alt="School Logo" style={{ height: '70px' }} />
                                        <h4 className="mt-2 mb-0" style={{ color: '#003366' }}>{schoolInfo.schoolName}</h4>
                                        <small className="text-muted d-block">{schoolInfo.schoolAddress}</small>
                                        <div className="mt-3 border-top border-bottom py-2">
                                            <h5>Examination Result</h5>
                                        </div>
                                    </div>

                                    <div className="bg-light p-3 rounded mb-4">
                                        <Row>
                                            <Col md="6">
                                                <p className="mb-1"><strong>Student Name:</strong> {result.fullName}</p>
                                                <p className="mb-1"><strong>Registration Number:</strong> {result.registrationNumber}</p>
                                                <p className="mb-1"><strong>Class:</strong> {classes.find(c => c._id === result.class)?.className || 'N/A'}</p>
                                            </Col>
                                            <Col md="6">
                                                <p className="mb-1"><strong>Exam Type:</strong> {examTypes.find(e => e._id === result.examType)?.name || 'N/A'}</p>
                                                <p className="mb-1"><strong>Academic Year:</strong> {result.year}</p>
                                                <p className="mb-1"><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="table-responsive">
                                        {(() => {
                                            const isComponent = Array.isArray(result.subjects) && result.subjects.some(s => Array.isArray(s.components) && s.components.length > 0);
                                            const componentSet = new Set();
                                            if (isComponent) {
                                                result.subjects.forEach(subject => {
                                                    (subject.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) componentSet.add(n); });
                                                    (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) componentSet.add(n); });
                                                });
                                            }
                                            const componentNames = Array.from(componentSet);

                                            return (
                                                <Table bordered hover className="shadow-sm">
                                                    <thead className="bg-primary text-white">
                                                        <tr>
                                                            {!isComponent ? (
                                                                <>
                                                                    <th>Subject</th>
                                                                    <th className="text-center">Total Marks</th>
                                                                    <th className="text-center">Obtained Marks</th>
                                                                    <th className="text-center">Grade</th>
                                                                    <th>Remarks</th>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <th>Subject</th>
                                                                    <th className="text-center">Subject Mid Marks</th>
                                                                    {componentNames.map(name => (
                                                                        <th key={`comp-h-${name}`} className="text-center">{name}</th>
                                                                    ))}
                                                                    <th className="text-center">Subject Final Marks</th>
                                                                    <th className="text-center">Total Marks</th>
                                                                    <th className="text-center">Total Obtained</th>
                                                                    <th className="text-center">Grade</th>
                                                                    <th>Remarks</th>
                                                                </>
                                                            )}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!isComponent ? (
                                                            result.subjects.map((subject, index) => {
                                                                const totalMarks = subject?.grandTotal ?? subject?.subjectTotalMarks ?? subject?.totalMarks ?? result?.totalMarks ?? 'N/A';
                                                                const obtainedMarks = subject?.grandObtained ?? subject?.outsideObtained ?? subject?.obtainedMarks ?? result?.obtainedMarks ?? 'N/A';
                                                                return (
                                                                    <tr key={`${subject?.subjectCode || index}`}>
                                                                        <td>{subject?.subjectName || 'N/A'}</td>
                                                                        <td className="text-center">{totalMarks}</td>
                                                                        <td className="text-center">{obtainedMarks}</td>
                                                                        <td className="text-center">
                                                                            <Badge color={getGradeColor(subject?.grade || 'N/A')} pill>
                                                                                {subject?.grade || 'N/A'}
                                                                            </Badge>
                                                                        </td>
                                                                        <td>{subject?.remarks || 'N/A'}</td>
                                                                    </tr>
                                                                );
                                                            })
                                                        ) : (
                                                            result.subjects.map((subject, index) => {
                                                                const totals = new Map();
                                                                (subject.components || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) totals.set(n, c?.totalMarks ?? '-'); });
                                                                const obtained = new Map();
                                                                (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName; if (n) obtained.set(n, c?.marks ?? '-'); });
                                                                const subjectMid = `${(subject.midSubjectObtainedMarks ?? '-')}/${(subject.midSubjectTotalMarks ?? '-')}`;
                                                                const subjectFinal = `${(subject.subjectObtainMarksFinal ?? '-')}/${(subject.subjectTotalMarksFinal ?? '-')}`;
                                                                const totalMarks = subject.grandTotal ?? subject.totalMarks ?? 'N/A';
                                                                const totalObtained = subject.grandObtained ?? subject.obtainedMarks ?? 'N/A';
                                                                return (
                                                                    <tr key={`${subject?.subjectCode || index}`}>
                                                                        <td>{subject?.subjectName || 'N/A'}</td>
                                                                        <td className="text-center">{subjectMid}</td>
                                                                        {componentNames.map(name => (
                                                                            <td key={`${subject?.subjectCode || index}-comp-${name}`} className="text-center">{`${(obtained.get(name) ?? '-')}/${(totals.get(name) ?? '-')}`}</td>
                                                                        ))}
                                                                        <td className="text-center">{subjectFinal}</td>
                                                                        <td className="text-center">{totalMarks}</td>
                                                                        <td className="text-center">{totalObtained}</td>
                                                                        <td className="text-center">
                                                                            <Badge color={getGradeColor(subject?.grade || 'N/A')} pill>
                                                                                {subject?.grade || 'N/A'}
                                                                            </Badge>
                                                                        </td>
                                                                        <td>{subject?.remarks || 'N/A'}</td>
                                                                    </tr>
                                                                );
                                                            })
                                                        )}
                                                    </tbody>
                                                </Table>
                                            );
                                        })()}
                                    </div>

                                    <div className="mt-4 p-3 border rounded shadow-sm">
                                        <Row>
                                            <Col md="8">
                                                <Row>
                                                    <Col sm="6" className="mb-2">
                                                        <p className="mb-0 font-weight-bold">Total Marks:</p>
                                                        <h5>{result?.totalMarks || 'N/A'}</h5>
                                                    </Col>
                                                    <Col sm="6" className="mb-2">
                                                        <p className="mb-0 font-weight-bold">Obtained Marks:</p>
                                                        <h5>{result?.obtainedMarks || 'N/A'}</h5>
                                                    </Col>
                                                    <Col sm="6">
                                                        <p className="mb-0 font-weight-bold">Overall Percentage:</p>
                                                        <h5>{result?.overallPercentage || 'N/A'}%</h5>
                                                    </Col>
                                                    <Col sm="6">
                                                        <p className="mb-0 font-weight-bold">Overall Grade:</p>
                                                        <h5>
                                                            <Badge color={getGradeColor(result.overallGrade)} pill className="px-3 py-2">
                                                                {result.overallGrade}
                                                            </Badge>
                                                        </h5>
                                                    </Col>
                                                    <Col sm="6" className="mt-2">
                                                        <p className="mb-0 font-weight-bold">Overall Rank (Class):</p>
                                                        <h5>{result?.rank ?? 'N/A'}</h5>
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col md="4" className="d-flex align-items-center justify-content-center">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: '120px',
                                                        height: '120px',
                                                        backgroundColor: getOverallColor(result.overallPercentage),
                                                        color: 'white'
                                                    }}
                                                >
                                                    <div className="text-center">
                                                        <h2 className="mb-0">{result.overallPercentage}%</h2>
                                                        <small>Score</small>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </CardBody>
                            </Card>
                        ) : (
                            <Card className="shadow border-0 bg-white h-100 d-flex align-items-center justify-content-center p-5">
                                <div className="text-center">
                                    <img
                                        src={logoUrl}
                                        alt="School Logo"
                                        style={{ height: '80px', opacity: 0.5 }}
                                        className="mb-4"
                                    />
                                    <h3 className="text-muted">Your Result Will Appear Here</h3>
                                    <p className="text-muted">
                                        Please enter your registration number, class, and exam type to view your results.
                                    </p>
                                </div>
                            </Card>
                        )}
                    </Col>
                </Row>

                <footer className="mt-5 text-center text-muted">
                    <small>&copy; {new Date().getFullYear()} {schoolInfo.schoolName}. All rights reserved.</small>
                    <div className="mt-1">
                        <small>{schoolInfo.schoolAddress} | {schoolInfo.schoolPhoneNumber} | {schoolInfo.schoolEmail}</small>
                    </div>
                </footer>
            </Container>
        </div>
    );
};

export default PublicResult;
